/**
 * Where you have actually been.
 *
 * Your own consecutive fixes come from one receiver with one bias, so the *shape* of your track
 * is far more trustworthy than any single absolute position on it — the same argument that lets
 * `sensors/heading-blend.ts` believe course over ground and `lib/closing.ts` believe a trend at a
 * range where it does not believe a bearing. That is what makes a trail honest enough to draw,
 * and it answers two questions nothing else in the product can: *am I going in circles*, and
 * *where have I already tried*.
 *
 * Three rules keep it that way.
 *
 * **Jitter is not a path.** A phone standing still with ten metres of accuracy produces a little
 * scribble, and drawn as a trail that scribble says you wandered around a room. So a point is
 * only kept once it is further from the last kept one than the fix could plausibly be wrong by.
 *
 * **A bad fix is not a point.** Anything the product would not point an arrow with does not get
 * to claim you were standing there either.
 *
 * **Nothing is written down.** The track lives in memory for the life of the screen and is never
 * stored, never sent, and never part of telemetry (`ProductOverview.md` §4.10). Closing the tab
 * is all it takes to destroy it, which is the same guarantee every other coordinate here has.
 */

import { haversineDistance, initialBearing, type LatLng } from '@beeline/shared';

export interface TrackPoint extends LatLng {
  /** Local clock when it was recorded. Never the device's idea of the time. */
  at: number;
}

/** A point placed relative to where you are standing now, in metres. */
export interface TrackOffset {
  /** Metres east and north of you. North-up: rotation happens at the drawing end. */
  east: number;
  north: number;
  /** How long ago, in milliseconds, so the drawing can fade it. */
  ageMs: number;
}

/**
 * How many points are worth keeping over a whole session.
 *
 * At the decimation distance below this is on the order of several kilometres of walking, which
 * is far past what this product is for. The oldest fall off the end.
 */
export const MAX_TRACK_POINTS = 400;

/** No point is ever kept closer than this to the last one, however good the fix claims to be. */
export const MIN_STEP_METRES = 8;

/**
 * How far you must move before it counts as movement.
 *
 * Two and a half times the reported accuracy: inside that, the difference between two fixes is
 * indistinguishable from the receiver arguing with itself.
 */
export const stepThresholdMetres = (accuracyMetres: number): number =>
  Math.max(MIN_STEP_METRES, accuracyMetres * 2.5);

export class Track {
  private readonly kept: TrackPoint[] = [];
  /** Running total of the kept path, so "how far have I walked" costs nothing to ask. */
  private walked = 0;

  get points(): readonly TrackPoint[] {
    return this.kept;
  }

  get length(): number {
    return this.kept.length;
  }

  /** Metres walked along the kept path. Decimated, so it under-reports rather than inflates. */
  get walkedMetres(): number {
    return this.walked;
  }

  /**
   * Offer a fix. Returns true when it became a point.
   *
   * Everything that decides *not* to keep it is a refusal to claim something the sensor did not
   * say: an unusable fix, or a step small enough to be noise.
   */
  record(
    fix: { lat: number; lng: number; accuracy: number; usable: boolean },
    now: number,
  ): boolean {
    if (!fix.usable) return false;

    const last = this.kept[this.kept.length - 1];
    if (last) {
      const moved = haversineDistance(last, fix);
      if (moved < stepThresholdMetres(fix.accuracy)) return false;
      this.walked += moved;
    }

    this.kept.push({ lat: fix.lat, lng: fix.lng, at: now });
    // The whole session, but not without bound: the oldest end goes first.
    if (this.kept.length > MAX_TRACK_POINTS) this.kept.shift();
    return true;
  }

  /**
   * The track as metres east and north of a given position, oldest first.
   *
   * Bearing and distance are exact great-circle values, so this is a proper local projection
   * rather than a flat-earth approximation that drifts. At the scale of a walk the difference is
   * academic; the reason to do it this way is that it reuses the geometry the rest of the app is
   * already trusted to get right.
   */
  offsetsFrom(origin: LatLng, now: number): TrackOffset[] {
    return this.kept.map((point) => {
      const distance = haversineDistance(origin, point);
      const bearing = initialBearing(origin, point);
      const radians = (bearing * Math.PI) / 180;
      return {
        east: distance * Math.sin(radians),
        north: distance * Math.cos(radians),
        ageMs: Math.max(0, now - point.at),
      };
    });
  }

  /** How far the furthest kept point is from a position — what a drawing has to fit inside. */
  spreadFrom(origin: LatLng): number {
    let furthest = 0;
    for (const point of this.kept) {
      const distance = haversineDistance(origin, point);
      if (distance > furthest) furthest = distance;
    }
    return furthest;
  }

  clear(): void {
    this.kept.length = 0;
    this.walked = 0;
  }
}
